/** Skripsi live queries + halt history. */

import { useMemo } from 'react';
import { COL } from '../../collections';
import { where, orderBy } from '../../collections';
import { useLiveQuery } from '../hooks';
import type { SkripsiDoc, SkripsiHistoryDoc } from '../../types';

export function useMySkripsi(userId: string | null) {
  return useLiveQuery<SkripsiDoc>(
    userId ? COL.skripsi : null,
    userId
      ? [
          where('mahasiswaId', '==', userId),
          where('status', '==', 'active'),
          orderBy('createdAt', 'desc'),
        ]
      : [],
    Boolean(userId),
  );
}

/** Every skripsi of a mahasiswa across statuses (active / cancelled / completed). */
export function useMySkripsiAll(userId: string | null) {
  return useLiveQuery<SkripsiDoc>(
    userId ? COL.skripsi : null,
    userId ? [where('mahasiswaId', '==', userId), orderBy('createdAt', 'desc')] : [],
    Boolean(userId),
  );
}

export function useAllSkripsi() {
  return useLiveQuery<SkripsiDoc>(COL.skripsi, [orderBy('createdAt', 'desc')]);
}

/**
 * A single skripsi by id. Uses a server-scoped `id` filter instead of loading
 * the whole collection, with a defensive client-side id match so correctness
 * holds even if the backend ignores the `id` constraint.
 */
export function useSkripsi(id: string | null) {
  const { data, loading, error } = useLiveQuery<SkripsiDoc>(
    id ? COL.skripsi : null,
    id ? [where('id', '==', id)] : [],
    Boolean(id),
  );
  const doc = useMemo(
    () => (data.length > 0 ? (data.find((s) => s.id === id) ?? data[0]) : null),
    [data, id],
  );
  return { data: doc, loading, error };
}

/** Halted-skripsi history records for a skripsi (latest first). */
export function useSkripsiHistory(skripsiId: string | null) {
  return useLiveQuery<SkripsiHistoryDoc>(
    skripsiId ? COL.skripsiHistory : null,
    skripsiId ? [where('skripsiId', '==', skripsiId), orderBy('createdAt', 'desc')] : [],
    Boolean(skripsiId),
  );
}
